import { Button, Modal, Space, Spin, Typography, notification } from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { ServiceCategory, getServiceCategories } from 'services/app-instance';

const { Text } = Typography;
const DEFAULT_SERVICE_CATEGORY: ServiceCategory = {
  id: 'default',
  name: 'Default',
  displayName: 'Default',
};

const withDefaultCategory = (items: ServiceCategory[]) => {
  if (items.some((category) => category.id === DEFAULT_SERVICE_CATEGORY.id)) {
    return items;
  }
  return [DEFAULT_SERVICE_CATEGORY, ...items];
};

interface DemoAppModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  onSelectCategory: (
    categoryId?: string
  ) => void | Promise<void | { message?: string }>;
}

export const DemoAppModal: React.FC<DemoAppModalProps> = ({
  isModalOpen,
  onClose,
  onSelectCategory,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [categoryList, setCategoryList] = useState<ServiceCategory[]>([DEFAULT_SERVICE_CATEGORY]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedCategoryId(null);
      return;
    }

    let isMounted = true;
    const fetchModalData = async () => {
      setIsLoading(true);
      try {
        const categories = await getServiceCategories();
        if (isMounted) {
          setCategoryList(withDefaultCategory(categories || []));
        }
      } catch (err: any) {
        console.error(err?.message);
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to load demo categories.',
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchModalData();
    return () => {
      isMounted = false;
    };
  }, [isModalOpen]);

  const handleSelectCategory = (category: ServiceCategory) => {
    setSelectedCategoryId(category.id);
    const categoryId = category.id === DEFAULT_SERVICE_CATEGORY.id ? undefined : category.id;

    Promise.resolve(onSelectCategory(categoryId)).finally(() => {
      setSelectedCategoryId(null);
    });
  };

  return (
    <Modal title="Choose a Demo Category" visible={isModalOpen} onCancel={onClose} footer={null}>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {categoryList.length ? (
            categoryList.map((category) => (
              <Button
                key={category.id}
                block
                loading={selectedCategoryId === category.id}
                onClick={() => handleSelectCategory(category)}
              >
                {category.displayName || category.name}
              </Button>
            ))
          ) : (
            <Text>No demo categories available.</Text>
          )}
        </Space>
      )}
    </Modal>
  );
};
